import { LitElement, html, css, nothing } from 'lit';
import { generationChanged } from '../utils/constants.js';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { ModelerController } from '../controllers/modeler-controller.js';

// ---------------------------------------------------------------------------
// <sysml-health-panel> -- Model health / completeness
//
// Ported from: SysmlHealthPanel.vue
//
// Features:
// - Overall score ring (SVG)
// - Validation rule results from evaluateValidationRules()
// - Metrics grid (partDefs, requirements, ports, etc.)
// - INCOSE quality indicators
// - Sub-scores (completeness, consistency, clarity)
// ---------------------------------------------------------------------------

/** Validation rule definition for local evaluation */
interface ValidationRule {
  id: string;
  name: string;
  description: string;
  severity: 'error' | 'warning' | 'info';
  check: (modeler: ModelerController) => { passed: boolean; count: number; total: number; details?: string };
}

/** Hardcoded validation rules matching INCOSE best practices */
const VALIDATION_RULES: ValidationRule[] = [
  {
    id: 'has-part-defs',
    name: 'Part Definitions Exist',
    description: 'Model should contain at least one part definition',
    severity: 'error',
    check: (m) => ({ passed: m.partDefinitions.length > 0, count: m.partDefinitions.length, total: 1 }),
  },
  {
    id: 'has-requirements',
    name: 'Requirements Defined',
    description: 'Model should contain at least one requirement',
    severity: 'warning',
    check: (m) => ({ passed: m.requirements.length > 0, count: m.requirements.length, total: 1 }),
  },
  {
    id: 'has-verification',
    name: 'Verification Cases',
    description: 'Requirements should have corresponding verification cases',
    severity: 'warning',
    check: (m) => {
      const reqs = m.requirements;
      const vcs = m.verificationCases;
      if (reqs.length === 0) return { passed: true, count: 0, total: 0, details: 'No requirements to verify' };
      const covered = reqs.filter((req) =>
        vcs.some(
          (vc) =>
            vc.subject?.ref === req.name ||
            vc.subject?.ref === req.id,
        ),
      ).length;
      return { passed: covered === reqs.length, count: covered, total: reqs.length, details: `${covered}/${reqs.length} requirements verified` };
    },
  },
  {
    id: 'has-ports',
    name: 'Port Definitions',
    description: 'Parts should define ports for inter-part communication',
    severity: 'info',
    check: (m) => ({ passed: m.ports.length > 0, count: m.ports.length, total: 1 }),
  },
  {
    id: 'has-connections',
    name: 'Connections Exist',
    description: 'Model should define connections between parts',
    severity: 'info',
    check: (m) => ({ passed: m.relationships.length > 0, count: m.relationships.length, total: 1 }),
  },
  {
    id: 'no-parse-errors',
    name: 'No Parse Errors',
    description: 'Source should parse without errors',
    severity: 'error',
    check: (m) => ({ passed: m.errorCount === 0, count: m.errorCount === 0 ? 1 : 0, total: 1, details: m.errorCount > 0 ? `${m.errorCount} error(s)` : undefined }),
  },
  {
    id: 'has-documentation',
    name: 'Elements Documented',
    description: 'Part definitions should have doc comments',
    severity: 'info',
    check: (m) => {
      const parts = m.partDefinitions;
      if (parts.length === 0) return { passed: true, count: 0, total: 0 };
      const documented = parts.filter((p) => !!p.meta?.notes).length;
      return { passed: documented === parts.length, count: documented, total: parts.length, details: `${documented}/${parts.length} documented` };
    },
  },
  {
    id: 'req-has-text',
    name: 'Requirements Have Text',
    description: 'Requirements should have descriptive doc comments',
    severity: 'warning',
    check: (m) => {
      const reqs = m.requirements;
      if (reqs.length === 0) return { passed: true, count: 0, total: 0 };
      const withDoc = reqs.filter((r) => !!r.text).length;
      return { passed: withDoc === reqs.length, count: withDoc, total: reqs.length, details: `${withDoc}/${reqs.length} have text` };
    },
  },
];

interface RuleResult {
  rule: ValidationRule;
  passed: boolean;
  count: number;
  total: number;
  details?: string;
}

@customElement('sysml-health-panel')
export class SysmlHealthPanel extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host { display: flex; flex-direction: column; height: 100%; overflow: hidden; }

      .score-ring-container {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--r5-space-4) var(--r5-space-3);
        gap: var(--r5-space-4);
      }
      .score-ring svg { width: 120px; height: 120px; }
      .score-ring text {
        font-size: var(--r5-text-2xl);
        font-weight: 700;
        fill: var(--r5-text);
        text-anchor: middle;
        dominant-baseline: central;
      }
      .score-ring .sub-text {
        font-size: var(--r5-text-2xs);
        font-weight: 400;
        fill: var(--r5-text-muted);
      }
      .score-ring .track { stroke: var(--r5-border); }
      .score-ring .fill { transition: stroke-dashoffset 0.6s ease; }

      .sub-scores {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-2);
      }
      .sub-score {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .sub-score-label {
        font-size: var(--r5-text-2xs);
        color: var(--r5-text-muted);
        font-weight: 500;
      }
      .sub-score-bar {
        height: 4px;
        border-radius: 2px;
        background: var(--r5-border);
        width: 100px;
        overflow: hidden;
      }
      .sub-score-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.5s ease;
      }

      /* -- Metrics Grid -- */
      .metrics {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: var(--r5-space-2);
        padding: 0 var(--r5-space-3) var(--r5-space-3);
      }
      .metric {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: var(--r5-space-2);
        background: var(--r5-surface-muted);
        border-radius: var(--r5-radius-md);
        text-align: center;
      }
      .metric-value {
        font-size: var(--r5-text-base);
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .metric-label {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        line-height: 1.2;
      }

      /* -- Validation Rules -- */
      .rules-section {
        flex: 1;
        overflow-y: auto;
        border-top: 1px solid var(--r5-border);
      }
      .rules-header {
        padding: var(--r5-space-2) var(--r5-space-3);
        font-size: var(--r5-text-xs);
        font-weight: 600;
        color: var(--r5-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background: var(--r5-surface-muted);
        border-bottom: 1px solid var(--r5-border);
        position: sticky;
        top: 0;
        z-index: 1;
      }
      .rule-row {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        font-size: var(--r5-text-xs);
      }
      .rule-icon {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--r5-font-size-badge);
        font-weight: 700;
        flex-shrink: 0;
      }
      .rule-icon.pass { background: var(--r5-success-50); color: var(--r5-success-700); }
      .rule-icon.fail { background: var(--r5-destructive-50); color: var(--r5-destructive-700); }
      .rule-icon.warn { background: var(--r5-warning-50); color: var(--r5-warning-700); }
      .rule-info {
        flex: 1;
        min-width: 0;
      }
      .rule-name {
        font-weight: 500;
      }
      .rule-details {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        margin-top: 1px;
      }
      .rule-badge {
        flex-shrink: 0;
      }
    `,
  ];

  // CRITICAL: hasChanged: generationChanged for controller properties
  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;

  @state() private _showDetails = false;

  override render() {
    const results = this._evaluateRules();
    const score = this._computeOverallScore(results);
    const subScores = this._computeSubScores(results);

    return html`
      <div class="panel-header">
        <h3>Model Health</h3>
        <ark-button size="xs" variant="ghost" @click=${() => (this._showDetails = !this._showDetails)}>
          ${this._showDetails ? 'Summary' : 'Details'}
        </ark-button>
      </div>

      <div style="overflow-y: auto; flex: 1;">
        ${this._renderScoreRing(score, subScores)}
        ${this._renderMetrics()}
        ${this._renderRules(results)}
      </div>
    `;
  }

  private _renderScoreRing(
    score: number,
    subScores: { completeness: number; consistency: number; clarity: number },
  ) {
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const color =
      score >= 70 ? 'var(--r5-success-500)'
      : score >= 40 ? 'var(--r5-warning-500)'
      : 'var(--r5-destructive-500)';

    const label =
      score >= 70 ? 'Good' : score >= 40 ? 'Fair' : 'Needs Work';

    return html`
      <div class="score-ring-container">
        <div class="score-ring">
          <svg viewBox="0 0 120 120">
            <circle class="track" cx="60" cy="60" r="${radius}" fill="none" stroke-width="8" />
            <circle
              class="fill"
              cx="60" cy="60" r="${radius}"
              fill="none"
              stroke=${color}
              stroke-width="8"
              stroke-linecap="round"
              stroke-dasharray=${circumference}
              stroke-dashoffset=${offset}
              transform="rotate(-90 60 60)"
            />
            <text x="60" y="55">${score}%</text>
            <text class="sub-text" x="60" y="73">${label}</text>
          </svg>
        </div>

        <div class="sub-scores">
          ${this._renderSubScore('Completeness', subScores.completeness)}
          ${this._renderSubScore('Consistency', subScores.consistency)}
          ${this._renderSubScore('Clarity', subScores.clarity)}
        </div>
      </div>
    `;
  }

  private _renderSubScore(label: string, value: number) {
    const color =
      value >= 70 ? 'var(--r5-success-500)'
      : value >= 40 ? 'var(--r5-warning-500)'
      : 'var(--r5-destructive-500)';

    return html`
      <div class="sub-score">
        <span class="sub-score-label">${label} ${value}%</span>
        <div class="sub-score-bar">
          <div class="sub-score-fill" style="width: ${value}%; background: ${color};"></div>
        </div>
      </div>
    `;
  }

  private _renderMetrics() {
    const m = this.modeler;
    const metrics = [
      { label: 'Parts', value: m.partDefinitions.length },
      { label: 'Ports', value: m.ports.length },
      { label: 'Connections', value: m.relationships.length },
      { label: 'Requirements', value: m.requirements.length },
      { label: 'Verifications', value: m.verificationCases.length },
      { label: 'Errors', value: m.errorCount },
    ];

    return html`
      <div class="metrics">
        ${metrics.map(
          (metric) => html`
            <div class="metric">
              <span class="metric-value" style="${metric.label === 'Errors' && metric.value > 0 ? 'color: var(--r5-destructive-700)' : ''}">
                ${metric.value}
              </span>
              <span class="metric-label">${metric.label}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  private _renderRules(results: RuleResult[]) {
    const passed = results.filter((r) => r.passed);
    const failed = results.filter((r) => !r.passed);

    return html`
      <div class="rules-section">
        ${failed.length > 0 ? html`
          <div class="rules-header">Issues (${failed.length})</div>
          ${failed.map((r) => this._renderRuleRow(r))}
        ` : nothing}

        <div class="rules-header">
          ${failed.length > 0 ? `Passing (${passed.length})` : `All Rules Passing (${passed.length})`}
        </div>
        ${passed.map((r) => this._renderRuleRow(r))}
      </div>
    `;
  }

  private _renderRuleRow(result: RuleResult) {
    const iconClass = result.passed
      ? 'pass'
      : result.rule.severity === 'error'
        ? 'fail'
        : 'warn';
    const iconSvg = result.passed
      ? html`<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" style="width:12px;height:12px"><path d="M3 8l3.5 3.5L13 5"/></svg>`
      : result.rule.severity === 'error'
        ? html`<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" style="width:12px;height:12px"><path d="M4 4l8 8M12 4l-8 8"/></svg>`
        : html`<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" style="width:12px;height:12px"><path d="M8 4v5M8 11v1"/></svg>`;

    const severityBadge = result.passed
      ? 'badge-success'
      : result.rule.severity === 'error'
        ? 'badge-error'
        : result.rule.severity === 'warning'
          ? 'badge-warning'
          : 'badge-info';

    return html`
      <div class="rule-row">
        <span class="rule-icon ${iconClass}">${iconSvg}</span>
        <div class="rule-info">
          <div class="rule-name">${result.rule.name}</div>
          ${this._showDetails || !result.passed
            ? html`
                <div class="rule-details">
                  ${result.details ?? result.rule.description}
                </div>
              `
            : nothing}
        </div>
        <span class="badge ${severityBadge} rule-badge">
          ${result.passed ? 'Pass' : result.rule.severity}
        </span>
      </div>
    `;
  }

  // -- Evaluation Logic -----------------------------------------------

  private _evaluateRules(): RuleResult[] {
    return VALIDATION_RULES.map((rule) => {
      const result = rule.check(this.modeler);
      return {
        rule,
        passed: result.passed,
        count: result.count,
        total: result.total,
        details: result.details,
      };
    });
  }

  private _computeOverallScore(results: RuleResult[]): number {
    if (!this.modeler.parsedModel) return 0;
    if (results.length === 0) return 0;

    // Weighted: errors=3, warnings=2, info=1
    let totalWeight = 0;
    let passedWeight = 0;
    for (const r of results) {
      const w = r.rule.severity === 'error' ? 3 : r.rule.severity === 'warning' ? 2 : 1;
      totalWeight += w;
      if (r.passed) passedWeight += w;
    }
    return totalWeight > 0 ? Math.round((passedWeight / totalWeight) * 100) : 0;
  }

  private _computeSubScores(results: RuleResult[]): {
    completeness: number;
    consistency: number;
    clarity: number;
  } {
    const m = this.modeler;
    if (!m.parsedModel) return { completeness: 0, consistency: 0, clarity: 0 };

    // Completeness: how many expected artifacts are present
    let compScore = 0;
    let compTotal = 5;
    if (m.partDefinitions.length > 0) compScore++;
    if (m.requirements.length > 0) compScore++;
    if (m.verificationCases.length > 0) compScore++;
    if (m.ports.length > 0) compScore++;
    if (m.relationships.length > 0) compScore++;

    // Consistency: no errors, verification coverage
    let consScore = 0;
    let consTotal = 3;
    if (m.errorCount === 0) consScore++;
    if (m.warningCount === 0) consScore++;
    const reqsCovered = m.requirements.length > 0
      ? m.requirements.filter((req) =>
          m.verificationCases.some(
            (vc) =>
              vc.subject?.ref === req.name ||
              vc.subject?.ref === req.id,
          ),
        ).length / m.requirements.length
      : 1;
    if (reqsCovered >= 0.8) consScore++;

    // Clarity: documentation presence
    let clarScore = 0;
    let clarTotal = 2;
    const partsDocumented = m.partDefinitions.length > 0
      ? m.partDefinitions.filter((p) => !!p.meta?.notes).length / m.partDefinitions.length
      : 1;
    if (partsDocumented >= 0.5) clarScore++;
    const reqsDocumented = m.requirements.length > 0
      ? m.requirements.filter((r) => !!r.text).length / m.requirements.length
      : 1;
    if (reqsDocumented >= 0.5) clarScore++;

    return {
      completeness: Math.round((compScore / compTotal) * 100),
      consistency: Math.round((consScore / consTotal) * 100),
      clarity: Math.round((clarScore / clarTotal) * 100),
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-health-panel': SysmlHealthPanel;
  }
}
